import React from 'react';

import { ArrowRight } from 'lucide-react';
import { NavLink } from 'react-router-dom';

/**
 * The legend block — the survey world's own device for a set of items, and the
 * hub's answer to what used to be a grid of cards.
 *
 * DESIGN.md bans three things this replaces at once: the card (No-Card Rule),
 * the shadow (No-Shadow Rule) and the row of equal icon-heading-text tiles (the
 * Three-Up Ban, which names that grid as "the association default this page
 * exists to refuse"). A survey sheet keys its symbols in a legend, so a bounded
 * key with a head rule and one keyed row per item is the shape that belongs
 * here. Structure is carried entirely by hairlines.
 */

interface LegendBlockProps {
  /** The legend head. The One Label Rule's single label style — this is one of its two uses. */
  head: string;
  /**
   * Makes the head itself a link. Used by the reference rail, where each block is
   * a page that has pages under it and the heading is the way to open it. Omitted
   * on the landing, where the head is a category word and opens nothing.
   */
  to?: string;
  children: React.ReactNode;
  className?: string;
  /**
   * Set for a list of `LegendKey`s, which is what pads the list and lays it out in
   * one or two columns. Two pairs the keys up on wide screens, the way a printed
   * legend does when its list is short enough to sit beside itself — but only
   * where there is width for it: inside the reference rail two columns leave about
   * 140px a title and truncate nearly all of them, so the rail asks for one.
   *
   * Left unset for a list of `LegendRow`s, whose fixed title column and
   * description need the full width and which carry their own padding.
   */
  columns?: 1 | 2;
}

/** Inset for a keyed list, so the rows sit under the head rather than against the border. */
const LEGEND_LIST = 'px-5 py-2 sm:px-7';

const LEGEND_HEAD =
  'block border-b border-rule-strong px-5 py-3 font-ui text-step--2 font-medium uppercase tracking-[0.18em] sm:px-7';

export const LegendBlock: React.FC<LegendBlockProps> = ({
  head,
  to,
  children,
  className = '',
  columns,
}) => (
  <section className={`border border-rule-strong ${className}`}>
    {/* The head keeps its label styling either way — a linked head is the same
        head, not a second kind of thing. Only the hover tint marks it as one. */}
    {to ? (
      <h2>
        <NavLink
          to={to}
          className={({ isActive }): string =>
            `${LEGEND_HEAD} transition-colors duration-fast ease-sheet ${
              isActive ? 'text-apricot' : 'text-ink-muted hover:text-apricot'
            }`
          }
        >
          {head}
        </NavLink>
      </h2>
    ) : (
      <h2 className={`${LEGEND_HEAD} text-ink-muted`}>{head}</h2>
    )}
    <ul
      className={
        // `gap-x-8` rather than the 12 this started at — 48px of gutter is width
        // the titles need more than the gap does. On the landing, where the block
        // is wide, the 16px difference doesn't register.
        // The `nth-last-child(2):nth-child(odd)` rule drops the bottom hairline on
        // the left cell of the final pair, so a list with an odd count doesn't
        // leave one rule hanging under an empty cell. It belongs here and not on
        // `LegendKey`, because it is only true at two columns: in a single column
        // that same selector matches the *second-to-last* row of any even-length
        // list and would erase a rule in the middle of the list.
        columns === 2
          ? `grid grid-cols-1 gap-x-8 ${LEGEND_LIST} md:grid-cols-2 md:[&>li:nth-last-child(2):nth-child(odd)]:border-b-0`
          : columns === 1
            ? LEGEND_LIST
            : undefined
      }
    >
      {children}
    </ul>
  </section>
);

/**
 * The tally column: how many things sit behind a row.
 *
 * ALWAYS RENDERED, even at zero, and at a fixed width. That is the whole point of
 * it being a component rather than an inline `{count && …}`: a conditional tally
 * lets the arrow beside it slide left on any row that has no number, so neither
 * the digits nor the arrows form a column. Reserving the slot costs nothing on an
 * empty row and makes both line up down the list.
 *
 * `w-8` fits three digits at this step — the largest section in the current tree
 * holds 158 pages. Right-aligned so the numbers agree on their units place, and
 * `tabular-nums` so a 1 and a 7 are the same width.
 *
 * Empty rather than "0" when there is nothing behind the row: a count of nothing
 * is not information.
 */
export const Tally: React.FC<{ count?: number; unit?: string }> = ({
  count,
  unit = 'items',
}) => (
  <span
    aria-hidden={count === undefined || count <= 0 ? 'true' : undefined}
    className="w-8 shrink-0 text-right font-ui text-step--2 font-medium tabular-nums tracking-[0.18em] text-ink-muted"
  >
    {count !== undefined && count > 0 && (
      <>
        {count}
        <span className="sr-only"> {unit}</span>
      </>
    )}
  </span>
);

interface LegendKeyProps {
  to: string;
  label: string;
  count?: number;
}

/**
 * The compact keyed row: a label and its tally, nothing else.
 *
 * `LegendRow`'s sibling for the case where the key is the whole point — a run of
 * sections read as a set rather than one at a time. No description and no preview,
 * because at two columns there is no width for either and the count already says
 * how much is behind the label.
 */
export const LegendKey: React.FC<LegendKeyProps> = ({ to, label, count }) => (
  <li className="border-b border-rule last:border-b-0">
    {/* `end` so only the page you are actually on is lit. Without it every
        ancestor on the path would light too, and in the reference rail — where a
        block's rows are all on one branch — that would mark half the list. */}
    <NavLink
      to={to}
      end
      className="group -mx-3 flex items-center gap-3 px-3 py-4 transition-colors duration-fast ease-sheet hover:bg-page-sunk"
    >
      {({ isActive }): React.ReactNode => (
        <>
          <span
            className={`min-w-0 flex-1 truncate font-ui text-step-0 ${
              isActive ? 'text-apricot' : 'text-ink'
            }`}
          >
            {label}
          </span>
          <Tally count={count} />
          <ArrowRight
            size={14}
            aria-hidden="true"
            className="shrink-0 text-ink-muted opacity-0 transition-all duration-fast ease-sheet group-hover:translate-x-0.5 group-hover:opacity-100"
          />
        </>
      )}
    </NavLink>
  </li>
);

interface LegendRowProps {
  to: string;
  title: string;
  description?: string;
  /**
   * How many things are behind this row. Rendered as the survey sheet's own device
   * — a keyed symbol carries its tally — in the chrome label style, immediately
   * after the title.
   *
   * NOT flush right in the title column, which is where this started and was wrong:
   * the fixed column put the number a constant 32px from the *description* and
   * anything up to 156px from the title it counts, so proximity read it into the
   * wrong column, and inconsistently — a title that wrapped hugged its number while
   * a short one abandoned it.
   *
   * Omitted or zero renders nothing: a count of nothing is not information, and an
   * archive that is merely empty should not announce it in the key.
   */
  count?: number;
  /** Optional detail under the description — a preview list, a date. */
  children?: React.ReactNode;
}

/**
 * One keyed row. The title column is fixed on wide screens so the titles form a
 * true key down the left edge rather than a ragged list, and collapses under the
 * body on narrow ones.
 */
export const LegendRow: React.FC<LegendRowProps> = ({
  to,
  title,
  description,
  count,
  children,
}) => (
  <li className="border-b border-rule last:border-b-0">
    <NavLink
      to={to}
      className="group grid gap-x-8 gap-y-2 px-5 py-5 transition-colors duration-fast ease-sheet sm:px-7 md:grid-cols-[14rem_1fr]"
    >
      {/* Flex at every width, not just narrow ones, so the tally can follow its title.
          The arrow is pushed to the far edge by `ml-auto` rather than by spreading the
          row, which is what keeps the number beside the word it belongs to. With no
          count and no arrow this is a single flex child and reads exactly as the block
          it replaced. */}
      <div className="flex items-start gap-3">
        <span className="font-ui text-step-1 text-ink">{title}</span>
        {count !== undefined && count > 0 && (
          <span className="mt-1.5 shrink-0 font-ui text-step--2 font-medium tabular-nums tracking-[0.18em] text-ink-muted">
            {count}
            <span className="sr-only"> items</span>
          </span>
        )}
        <ArrowRight
          size={16}
          aria-hidden="true"
          className="ml-auto mt-1 shrink-0 text-ink-muted transition-transform duration-fast ease-sheet group-hover:translate-x-0.5 md:hidden"
        />
      </div>

      <div className="min-w-0">
        {description && <p className="text-step-0 text-ink-muted">{description}</p>}
        {children}
      </div>
    </NavLink>
  </li>
);
